import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { formatCurrency } from '@/lib/utils';
import type { Company } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Globe, Mail, MapPin, Phone, TrendingUp, Users } from 'lucide-react';
import { useCallback } from 'react';

interface CompanyCardProps {
    company: Company;
}

export function CompanyCard({ company }: CompanyCardProps) {
    const { t } = useTranslations();

    const getPriorityColor = (priority?: string) => {
        switch (priority) {
            case 'high':
                return 'bg-danger-subtle text-danger-subtle-foreground border-border';
            case 'medium':
                return 'bg-warning-subtle text-warning-subtle-foreground border-border';
            case 'low':
                return 'bg-success-subtle text-success-subtle-foreground border-border';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    // Handle navigation with fallback for mobile
    const handleNavigation = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const isTouch = 'touches' in e || 'changedTouches' in e;
            if (isTouch || window.innerWidth < 768) {
                e.preventDefault();
                e.stopPropagation();
                router.visit(`/company/${company.id}`, {
                    preserveState: false,
                    preserveScroll: false,
                });
            }
        },
        [company.id],
    );

    // Ensure we have a valid ID
    if (!company?.id) {
        console.error('Company ID is missing:', company);
        return null;
    }

    return (
        <Link href={`/company/${company.id}`} className="touch-target block" onClick={handleNavigation} onTouchEnd={handleNavigation}>
            <Card className="mb-2 cursor-pointer transition-shadow hover:shadow-sm">
                <CardContent className="p-3">
                    <div className="flex flex-col gap-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-sm font-semibold">{company.name}</h3>
                                {company.company_code && <p className="text-muted-foreground mt-0.5 text-xs">{company.company_code}</p>}
                            </div>
                            <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                                {company.type && (
                                    <Badge variant="outline" className="px-1.5 py-0 text-xs">
                                        {settingLabel(t, 'company_types', company.type)}
                                    </Badge>
                                )}
                                {company.priority && (
                                    <Badge variant="outline" className={`px-1.5 py-0 text-xs ${getPriorityColor(company.priority)}`}>
                                        {settingLabel(t, 'company_priorities', company.priority)}
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-1">
                            {company.address && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{company.address}</span>
                                </div>
                            )}

                            {company.country && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{company.country}</span>
                                </div>
                            )}

                            {company.phone && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{company.phone}</span>
                                </div>
                            )}

                            {company.email && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{company.email}</span>
                                </div>
                            )}

                            {(company.revenue || company.employees) && (
                                <div className="flex items-center gap-3 pt-1">
                                    {company.revenue && (
                                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                                            <TrendingUp className="h-3.5 w-3.5 flex-shrink-0" />
                                            <span>{formatCurrency(company.revenue)}</span>
                                        </div>
                                    )}
                                    {company.employees && (
                                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                                            <Users className="h-3.5 w-3.5 flex-shrink-0" />
                                            <span>{company.employees}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
